(() => {
  const hoverMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const prefersReducedMotion = () => reducedMotionQuery.matches;
  const canHover = () => hoverMediaQuery.matches;

  const parseImages = (value) => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const initProductCardHover = (root = document) => {
    if (!canHover() || prefersReducedMotion()) return;

    root.querySelectorAll('.product-card').forEach((card) => {
      const container = card.querySelector('.product-card__image[data-images]');
      if (!container) return;
      if (container.dataset.hoverSliderInitialized) return;
      container.dataset.hoverSliderInitialized = 'true';

      const images = parseImages(container.dataset.images);
      if (images.length < 2) return;

      const img = container.querySelector('img');
      if (!img) return;

      const originalImage = {
        src: img.currentSrc || img.src,
        srcset: img.getAttribute('srcset'),
        sizes: img.getAttribute('sizes'),
      };
      let index = 0;
      let intervalId = null;
      let preloaded = false;

      const preload = () => {
        if (preloaded) return;
        preloaded = true;
        images.slice(1).forEach((src) => {
          const preloadImage = new Image();
          preloadImage.src = src;
        });
      };

      const start = () => {
        if (intervalId) return;
        preload();
        intervalId = window.setInterval(() => {
          index = (index + 1) % images.length;
          img.src = images[index];
          img.srcset = '';
          img.sizes = '';
        }, 500);
      };

      const stop = () => {
        if (intervalId) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        index = 0;
        img.src = originalImage.src;
        if (originalImage.srcset) {
          img.setAttribute('srcset', originalImage.srcset);
        } else {
          img.removeAttribute('srcset');
        }
        if (originalImage.sizes) {
          img.setAttribute('sizes', originalImage.sizes);
        } else {
          img.removeAttribute('sizes');
        }
      };

      card.addEventListener('mouseenter', start);
      card.addEventListener('mouseleave', stop);
    });
  };

  const initProductMediaSlider = (root = document) => {
    root.querySelectorAll('[data-product-slider]').forEach((slider) => {
      if (slider.dataset.sliderInitialized) return;
      slider.dataset.sliderInitialized = 'true';

      const track = slider.querySelector('[data-product-track]');
      const slides = Array.from(slider.querySelectorAll('[data-product-slide]'));
      const prevButton = slider.querySelector('[data-product-prev]');
      const nextButton = slider.querySelector('[data-product-next]');
      const dots = Array.from(slider.querySelectorAll('[data-product-dot]'));

      if (!track || slides.length < 2) return;

      let currentIndex = 0;
      let ticking = false;

      const clampIndex = (index) => Math.max(0, Math.min(index, slides.length - 1));

      const updateControls = (index) => {
        currentIndex = clampIndex(index);
        dots.forEach((dot, dotIndex) => {
          if (dotIndex === currentIndex) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
        if (prevButton) prevButton.disabled = currentIndex === 0;
        if (nextButton) nextButton.disabled = currentIndex === slides.length - 1;
      };

      const scrollToIndex = (index) => {
        const clampedIndex = clampIndex(index);
        track.scrollTo({
          left: track.clientWidth * clampedIndex,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
        updateControls(clampedIndex);
      };

      updateControls(0);

      if (prevButton) {
        prevButton.addEventListener('click', () => scrollToIndex(currentIndex - 1));
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => scrollToIndex(currentIndex + 1));
      }

      dots.forEach((dot, dotIndex) => {
        dot.addEventListener('click', () => scrollToIndex(dotIndex));
      });

      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / track.clientWidth);
          updateControls(index);
          ticking = false;
        });
      });

      slider.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollToIndex(currentIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollToIndex(currentIndex + 1);
        }
      });

      window.addEventListener('resize', () => scrollToIndex(currentIndex));
    });
  };

  const initCartQuantityControls = (root = document) => {
    root.querySelectorAll('.cart-item__qty-control').forEach((control) => {
      if (control.dataset.qtyControlInitialized) return;
      control.dataset.qtyControlInitialized = 'true';

      const input = control.querySelector('.cart-item__qty');
      if (!input) return;
      const decrementButton = control.querySelector('[data-qty-decrement]');
      const incrementButton = control.querySelector('[data-qty-increment]');
      const minValue = Number.parseInt(input.min, 10);
      const minQuantity = Number.isNaN(minValue) ? 1 : minValue;

      const adjustQuantity = (delta) => {
        const currentValue = Number.parseInt(input.value, 10);
        const safeValue = Number.isNaN(currentValue) ? minQuantity : currentValue;
        input.value = Math.max(minQuantity, safeValue + delta);
      };

      if (decrementButton) {
        decrementButton.addEventListener('click', () => adjustQuantity(-1));
      }

      if (incrementButton) {
        incrementButton.addEventListener('click', () => adjustQuantity(1));
      }
    });
  };

  const initSearchForms = (root = document) => {
    root.querySelectorAll('form[data-search-strict]').forEach((form) => {
      if (form.dataset.searchStrictInitialized) return;
      form.dataset.searchStrictInitialized = 'true';

      form.addEventListener('submit', (event) => {
        const displayInput = form.querySelector('input[name="q_display"]');
        const hiddenInput = form.querySelector('input[name="q"]');
        if (!displayInput || !hiddenInput) return;
        const rawValue = displayInput.value.trim();
        if (!rawValue) return;

        const terms = rawValue.split(/\s+/).filter(Boolean);
        if (!terms.length) return;

        const strictQuery = terms.map((term) => `"${term}"`).join(' AND ');
        hiddenInput.value = strictQuery;
      });
    });
  };

  const initHelpDropdown = (root = document) => {
    const dropdowns = Array.from(root.querySelectorAll('[data-help-dropdown]'));
    if (!dropdowns.length) return;

    const closeAll = (except = null) => {
      dropdowns.forEach((dropdown) => {
        if (dropdown === except) return;
        dropdown.classList.remove('is-open');
        const toggle = dropdown.querySelector('[data-help-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    };

    dropdowns.forEach((dropdown) => {
      if (dropdown.dataset.helpDropdownInitialized) return;
      dropdown.dataset.helpDropdownInitialized = 'true';

      const toggle = dropdown.querySelector('[data-help-toggle]');
      if (!toggle) return;

      const open = () => {
        closeAll(dropdown);
        dropdown.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      };

      const close = () => {
        dropdown.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      };

      dropdown.addEventListener('mouseenter', () => {
        if (!canHover()) return;
        open();
      });

      dropdown.addEventListener('focusin', open);

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        if (dropdown.classList.contains('is-open')) {
          close();
        } else {
          open();
        }
      });
    });

    if (document.body.dataset.helpDropdownDocumentInitialized) return;
    document.body.dataset.helpDropdownDocumentInitialized = 'true';

    const getAllDropdowns = () => Array.from(document.querySelectorAll('[data-help-dropdown]'));

    document.addEventListener('click', (event) => {
      const target = event.target;
      const activeDropdown = getAllDropdowns().find((dropdown) =>
        dropdown.classList.contains('is-open'),
      );
      if (!activeDropdown) return;
      if (!activeDropdown.contains(target)) {
        activeDropdown.classList.remove('is-open');
        const toggle = activeDropdown.querySelector('[data-help-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      getAllDropdowns().forEach((dropdown) => {
        if (!dropdown.classList.contains('is-open')) return;
        dropdown.classList.remove('is-open');
        const toggle = dropdown.querySelector('[data-help-toggle]');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
          toggle.focus();
        }
      });
    });
  };

  const initTheme = (root = document) => {
    initProductCardHover(root);
    initProductMediaSlider(root);
    initCartQuantityControls(root);
    initSearchForms(root);
    initHelpDropdown(root);
  };

  document.addEventListener('DOMContentLoaded', () => initTheme());
  document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
})();
